import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useLocale } from '../../hooks/useLocale';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import animateValue from '../../utils/animateValue';
import { stripCurrencySymbol, toDecimals } from '../../utils/helpers';
import { PlusIcon } from '../BalancePlusIcon/PlusIcon';

import './BalancePanel.scss';
import { BalancePlusContext } from './BalancePlusContext';
import { useShowDualBalance } from './useShowDualBalance';

// `showScPill` toggles the SC pill of the dual-balance layout. While playing a slot the SC (psc+bsc)
// lives inside the machine, so the header is told to surface only the cash balance.
export const Balance = observer(({ showScPill = true }: { showScPill?: boolean }) => {
  const { walletStore, coinTypeStore, gameStore } = useStores();
  const { localeCurrencySymbol } = useLocale();
  const onPress = useContext(BalancePlusContext);

  const enableBalanceAnimation = useFlag('balance_animation_enabled', true);

  const animationDuration = useMemo(() => (enableBalanceAnimation ? 1500 : 100), [enableBalanceAnimation]);

  const ref = useRef<HTMLSpanElement>(null);
  const cashRef = useRef<HTMLSpanElement>(null);

  const [prev, setPrev] = useState(0);
  const [prevCash, setPrevCash] = useState(0);

  const { currentCustomValue, currentCoinValue } = walletStore;
  const cash = walletStore.balance.cash;

  const showDualBalance = useShowDualBalance();

  useEffect(() => {
    const curr = currentCustomValue ?? currentCoinValue ?? 0;
    if (curr !== prev && ref.current) {
      setPrev(curr);
      animateValue(ref.current, prev, curr, animationDuration, coinTypeStore.coinType);
    }
  }, [prev, coinTypeStore.coinType, currentCustomValue, currentCoinValue, animationDuration]);

  // Count-up the cash balance the same way SC/CC animate. `showDualBalance` is a dependency so the
  // effect re-fires once the cash span mounts (the LD flag can resolve after the first commit),
  // otherwise the value-change that filled the balance would have happened while cashRef was null.
  useEffect(() => {
    if (cash !== prevCash && cashRef.current) {
      setPrevCash(cash);
      animateValue(cashRef.current, prevCash, cash, animationDuration, 'SC', true);
    }
  }, [showDualBalance, cash, prevCash, animationDuration]);

  const coinContent = (
    <>
      <div className="balance-panel-left">{coinTypeStore.coinType === 'GC' ? 'CC' : 'SC'}</div>
      <span ref={ref} className="balance-panel-balance" data-testid="lobby-balance-bar" id="balance">
        {prev.toLocaleString()}
      </span>
    </>
  );

  if (!showDualBalance) {
    return coinContent;
  }

  // Big cash values widen the amount and push the SC pill / store button off a narrow (mobile)
  // header. Shrink the font in tiers by the formatted length so it fits instead of overflowing.
  const cashLength = toDecimals(cash).length;
  const cashClassName = classNames('balance-panel-cash', {
    'balance-panel-cash--sm': cashLength >= 10,
    'balance-panel-cash--md': cashLength >= 7 && cashLength < 10,
  });

  return (
    <>
      <div className={cashClassName} data-testid="lobby-cash-balance">
        <span className="balance-panel-cash-sign" data-testid="lobby-cash-sign">
          {stripCurrencySymbol(localeCurrencySymbol())}
        </span>
        {gameStore.isReloadingCash && (
          <span aria-label="Updating balance" className="balance-panel-cash-reload" data-testid="lobby-cash-reload">
            <span className="balance-panel-cash-reload-dot" />
            <span className="balance-panel-cash-reload-dot" />
            <span className="balance-panel-cash-reload-dot" />
          </span>
        )}
        {/* Keep cashRef mounted (hide while reloading) — unmounting it mid-animateValue rAF races
            React's removeChild during the poll's isReloadingCash swap and crashes the SC lobby. */}
        <span
          ref={cashRef}
          className="balance-panel-cash-amount"
          data-testid="lobby-cash-amount"
          style={gameStore.isReloadingCash ? { display: 'none' } : undefined}
        >
          {toDecimals(prevCash)}
        </span>
      </div>
      {showScPill && (
        <div className="balance-panel-sc-pill" data-testid="lobby-sc-pill">
          {coinContent}
          {onPress && (
            <div className="balance-panel-sc-pill-plus">
              <PlusIcon onPress={onPress} />
            </div>
          )}
        </div>
      )}
    </>
  );
});
